import { defineStore } from 'pinia';
import { agentApiService } from 'src/services';
import type {
  Agent,
  AgentCreate,
  SkillDefinition,
  McpServerInfo,
} from 'src/@types/agents';

export interface AgentsState {
  agents: Agent[];
  availableSkills: SkillDefinition[];
  mcpServerNames: Record<string, string>;
  /** Tools backend has flagged as ``critical`` — i.e., always
   *  require human approval under the default ``critical``
   *  approval mode.  Cached for the session via
   *  ``fetchToolMetadata``; backend's ``CRITICAL_TOOLS`` is
   *  canonical.  An empty set means we haven't fetched yet (or
   *  the fetch failed) — UIs should treat that as "no badge"
   *  rather than "no critical tools". */
  criticalTools: Set<string>;
  /** Per-agent ``tool_approvals`` overrides (effective values: org
   *  overlay merged on top of the agent's own config).  Keyed by
   *  agentId.  Populated lazily by ``fetchToolApprovals(agentId)``;
   *  mutated locally on PUT/DELETE so badge UIs flip immediately. */
  toolApprovalsByAgent: Record<string, Record<string, boolean>>;
  currentAgent: Agent | null;
  processing: boolean;
  error: string | null;
}

export const useAgentsStore = defineStore('agents', {
  state: (): AgentsState => ({
    agents: [],
    availableSkills: [],
    mcpServerNames: {},
    criticalTools: new Set<string>(),
    toolApprovalsByAgent: {},
    currentAgent: null,
    processing: false,
    error: null,
  }),

  getters: {
    /**
     * Get only enabled agents
     */
    enabledAgents: (state): Agent[] => state.agents.filter((a) => a.is_enabled),

    /**
     * Get agents by document type
     */
    getAgentsByDocType:
      (state) =>
      (docType: string): Agent[] =>
        state.agents.filter((a) => a.document_types.includes(docType)),

    /**
     * Get agent by ID
     */
    getAgentById:
      (state) =>
      (id: string): Agent | undefined =>
        state.agents.find((a) => a.id === id),

    /**
     * Get skill by ID
     */
    getSkillById:
      (state) =>
      (id: string): SkillDefinition | undefined =>
        state.availableSkills.find((s) => s.id === id),

    /**
     * Whether a tool is in the backend's ``CRITICAL_TOOLS`` set
     * — UIs use this to decorate tool chips with a warning
     * indicator.  Returns false until ``fetchToolMetadata`` has
     * populated the cache, so callers should fire that fetcher
     * on mount.
     */
    isCriticalTool:
      (state) =>
      (name: string | null | undefined): boolean => {
        if (!name) return false;
        return state.criticalTools.has(name);
      },

    /**
     * Per-agent override map (or ``{}`` if not yet fetched).
     */
    getToolApprovalsForAgent:
      (state) =>
      (agentId: string | null | undefined): Record<string, boolean> => {
        if (!agentId) return {};
        return state.toolApprovalsByAgent[agentId] || {};
      },

    /**
     * Effective gating for a tool on an agent given an
     * ``approval_mode``.  Mirrors backend ``hitl.needs_approval``:
     *
     * - ``all``  → always true (overrides ignored)
     * - ``none`` → always false (overrides ignored)
     * - ``critical`` → override wins, else fall back to
     *   ``CRITICAL_TOOLS``
     *
     * Used by badge UIs to color the chip and by override popovers
     * to render the current state.
     */
    isToolApprovalRequired() {
      return (
        toolName: string,
        agentId: string | null | undefined,
        approvalMode: string,
      ): boolean => {
        if (approvalMode === 'all') return true;
        if (approvalMode === 'none') return false;
        const overrides = agentId ? this.toolApprovalsByAgent[agentId] || {} : {};
        if (toolName in overrides) return overrides[toolName] === true;
        return this.criticalTools.has(toolName);
      };
    },

    /**
     * Check if processing
     */
    isProcessing: (state): boolean => state.processing,

    /**
     * Kanban board getters - derive status from agent fields
     * System agents are included in the appropriate column based on their status
     */
    activeAgents: (state): Agent[] =>
      state.agents.filter(
        (a) => a.is_enabled && a.triggers?.some((t) => t.is_enabled),
      ),

    idleAgents: (state): Agent[] =>
      state.agents.filter(
        (a) =>
          a.is_enabled &&
          (!a.triggers?.length || a.triggers.every((t) => !t.is_enabled)),
      ),

    disabledAgents: (state): Agent[] =>
      state.agents.filter((a) => !a.is_enabled),
  },

  actions: {
    /**
     * Fetch all agents for the organization
     */
    async fetchAgents(filters?: {
      is_enabled?: boolean;
      document_type?: string;
    }): Promise<void> {
      this.processing = true;
      this.error = null;
      try {
        this.agents = await agentApiService.getAll(filters);
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch agents';
        throw err;
      } finally {
        this.processing = false;
      }
    },

    /**
     * Fetch a single agent by ID
     */
    async fetchAgent(id: string): Promise<Agent> {
      this.processing = true;
      this.error = null;
      try {
        const agent = await agentApiService.getById(id);
        this.currentAgent = agent;

        // Update in agents list if exists
        const index = this.agents.findIndex((a) => a.id === id);
        if (index >= 0) {
          this.agents[index] = agent;
        }

        return agent;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch agent';
        throw err;
      } finally {
        this.processing = false;
      }
    },

    /**
     * Create a new agent
     */
    async createAgent(data: AgentCreate): Promise<Agent> {
      this.processing = true;
      this.error = null;
      try {
        const agent = await agentApiService.create(data);
        this.agents.push(agent);
        this.currentAgent = agent;
        return agent;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to create agent';
        throw err;
      } finally {
        this.processing = false;
      }
    },

    /**
     * Update an existing agent
     */
    async updateAgent(id: string, data: Partial<AgentCreate>): Promise<Agent> {
      this.processing = true;
      this.error = null;
      try {
        const agent = await agentApiService.update(id, data);

        // Update in agents list
        const index = this.agents.findIndex((a) => a.id === id);
        if (index >= 0) {
          this.agents[index] = agent;
        }

        if (this.currentAgent?.id === id) {
          this.currentAgent = agent;
        }

        return agent;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to update agent';
        throw err;
      } finally {
        this.processing = false;
      }
    },

    /**
     * Delete an agent
     */
    async deleteAgent(id: string): Promise<void> {
      this.processing = true;
      this.error = null;
      try {
        await agentApiService.deleteAgent(id);

        // Remove from agents list
        const index = this.agents.findIndex((a) => a.id === id);
        if (index >= 0) {
          this.agents.splice(index, 1);
        }

        if (this.currentAgent?.id === id) {
          this.currentAgent = null;
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to delete agent';
        throw err;
      } finally {
        this.processing = false;
      }
    },

    /**
     * Toggle agent enabled/disabled status
     */
    async toggleAgent(id: string, enabled: boolean): Promise<Agent> {
      this.processing = true;
      this.error = null;
      try {
        const agent = await agentApiService.toggle(id, enabled, '');

        // Update in agents list
        const index = this.agents.findIndex((a) => a.id === id);
        if (index >= 0) {
          this.agents[index] = agent;
        }

        if (this.currentAgent?.id === id) {
          this.currentAgent = agent;
        }

        return agent;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to toggle agent';
        throw err;
      } finally {
        this.processing = false;
      }
    },

    /**
     * Fetch available skills
     */
    async fetchSkills(): Promise<void> {
      this.processing = true;
      this.error = null;
      try {
        this.availableSkills = await agentApiService.getSkills();
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch skills';
        throw err;
      } finally {
        this.processing = false;
      }
    },

    /**
     * Fetch tool-classification metadata once per session.
     *
     * Populates ``criticalTools`` from the backend's canonical
     * ``CRITICAL_TOOLS`` list.  Errors are swallowed — a failed
     * fetch leaves the set empty, which means UIs render no
     * critical badges rather than crashing.  A reload retry can
     * recover.
     *
     * Re-fetching is cheap to make idempotent: callers can invoke
     * this from any page that wants the metadata; the network
     * round-trip is tiny but not deduplicated here (Pinia stores
     * are session-scoped, so the natural call shape is "once on
     * mount of a tools-rendering surface").
     */
    async fetchToolMetadata(): Promise<void> {
      try {
        const meta = await agentApiService.getToolMetadata();
        this.criticalTools = new Set(meta.critical || []);
      } catch (err) {
        // Don't surface as ``this.error`` — this is auxiliary
        // metadata, not the page's primary load.  Logging is
        // enough; the UI degrades gracefully (no badges).
        console.error('[agents] fetchToolMetadata failed:', err);
      }
    },

    /**
     * Fetch the effective ``tool_approvals`` overrides for an agent
     * (org overlay merged on top of the agent's own config) and
     * cache them in ``toolApprovalsByAgent``.
     *
     * Errors are swallowed — the override map is purely additive
     * UX (it lets the badge popover show the current state); on
     * fetch failure callers fall back to ``CRITICAL_TOOLS`` defaults.
     */
    async fetchToolApprovals(agentId: string): Promise<Record<string, boolean>> {
      try {
        const data = await agentApiService.getToolApprovals(agentId);
        const map = data.tool_approvals || {};
        this.toolApprovalsByAgent = { ...this.toolApprovalsByAgent, [agentId]: map };
        return map;
      } catch (err) {
        console.error('[agents] fetchToolApprovals failed:', err);
        return {};
      }
    },

    /**
     * Pin a per-tool override on an agent.  Updates the local
     * cache so the badge UI flips immediately without a re-fetch.
     */
    async setToolApproval(
      agentId: string,
      toolName: string,
      requireApproval: boolean,
    ): Promise<void> {
      const data = await agentApiService.setToolApproval(agentId, toolName, requireApproval);
      this.toolApprovalsByAgent = {
        ...this.toolApprovalsByAgent,
        [agentId]: data.tool_approvals || {},
      };
    },

    /**
     * Clear a per-tool override (revert to default ``CRITICAL_TOOLS``
     * gating under ``critical`` mode).
     */
    async clearToolApproval(agentId: string, toolName: string): Promise<void> {
      const data = await agentApiService.deleteToolApproval(agentId, toolName);
      this.toolApprovalsByAgent = {
        ...this.toolApprovalsByAgent,
        [agentId]: data.tool_approvals || {},
      };
    },

    /**
     * Fetch MCP servers and cache their display names.
     */
    async fetchMcpServerNames(): Promise<void> {
      try {
        const servers: McpServerInfo[] = await agentApiService.discoverMcpServers();
        const names: Record<string, string> = {};
        for (const s of servers) {
          names[s.id] = s.name;
        }
        this.mcpServerNames = names;
      } catch {
        // Non-critical — display views fall back to raw ID
      }
    },

    /**
     * Get display name for an MCP server ID.
     */
    getMcpServerName(serverId: string): string {
      return this.mcpServerNames[serverId] || serverId;
    },

    /**
     * Set current agent
     */
    setCurrentAgent(agent: Agent | null): void {
      this.currentAgent = agent;
    },

    /**
     * Clear error
     */
    clearError(): void {
      this.error = null;
    },

    /**
     * Reset store state
     */
    reset(): void {
      this.agents = [];
      this.availableSkills = [];
      this.mcpServerNames = {};
      this.currentAgent = null;
      this.processing = false;
      this.error = null;
    },
  },
});
